import type { CascadeStep } from '@/services/api/teacher'

interface CascadeIndicatorProps {
  steps: CascadeStep[]
  viewerRank?: number | null
  viewerIsLastRemaining?: boolean
  isDirectAssignment?: boolean
  compact?: boolean
}

export function CascadeIndicator({
  steps,
  viewerRank,
  viewerIsLastRemaining,
  isDirectAssignment,
  compact = false,
}: CascadeIndicatorProps) {
  if (isDirectAssignment) {
    return (
      <div className="mt-2 text-xs text-gray-500 italic">
        Direkt zugewiesen
      </div>
    )
  }

  if (steps.length === 0) {
    return null
  }

  const shouldCondense = steps.length > 6
  const displaySteps = shouldCondense ? condenseSteps(steps, viewerRank) : steps

  const ariaLabel = buildAriaLabel(steps, viewerRank)

  return (
    <div className="mt-3" role="img" aria-label={ariaLabel}>
      <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-2">
        Prioritätskaskade
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {displaySteps.map((step, index) => (
          <div key={'rank' in step ? `step-${step.rank}-${step.status}` : `ellipsis-${index}`} className="flex items-center gap-2">
            <StepPoint step={step} compact={compact} />
            {index < displaySteps.length - 1 && <Connector />}
          </div>
        ))}
      </div>
      {viewerIsLastRemaining && (
        <div className="mt-2 text-xs text-gray-500 italic">
          Alle anderen haben abgelehnt
        </div>
      )}
    </div>
  )
}

function condenseSteps(steps: CascadeStep[], viewerRank?: number | null): (CascadeStep | { type: 'ellipsis'; count: number })[] {
  const viewerIndex = viewerRank != null ? steps.findIndex(s => s.rank === viewerRank) : -1
  
  if (viewerIndex === -1) {
    const first = steps[0]
    const last = steps[steps.length - 1]
    if (!first || !last) return steps
    return [
      first,
      { type: 'ellipsis', count: steps.length - 2 },
      last,
    ]
  }

  const result: (CascadeStep | { type: 'ellipsis'; count: number })[] = []
  
  if (viewerIndex > 0) {
    const first = steps[0]
    if (first) result.push(first)
    if (viewerIndex > 1) {
      result.push({ type: 'ellipsis', count: viewerIndex - 1 })
    }
  }
  
  const viewer = steps[viewerIndex]
  if (viewer) result.push(viewer)
  
  if (viewerIndex < steps.length - 1) {
    if (viewerIndex < steps.length - 2) {
      result.push({ type: 'ellipsis', count: steps.length - viewerIndex - 2 })
    }
    const last = steps[steps.length - 1]
    if (last) result.push(last)
  }
  
  return result
}

function buildAriaLabel(steps: CascadeStep[], viewerRank?: number | null): string {
  const declinedCount = steps.filter(
    s => s.status === 'declined' || s.status === 'cancelled' || s.status === 'recontacted_declined'
  ).length
  const timedOutCount = steps.filter(
    s => s.status === 'timed_out' || s.status === 'recontacted_timed_out'
  ).length
  const contactedCount = steps.filter(
    s => s.status === 'contacted' || s.status === 'recontacted'
  ).length
  const waitingCount = steps.filter(s => s.status === 'waiting').length
  const pendingCount = steps.filter(s => s.status === 'pending').length
  const notSelectedCount = steps.filter(s => s.status === 'accepted_not_selected').length
  
  const parts: string[] = []
  if (declinedCount > 0) parts.push(`${declinedCount} abgelehnt`)
  if (timedOutCount > 0) parts.push(`${timedOutCount} zeit abgelaufen`)
  if (contactedCount > 0) parts.push(`${contactedCount} kontaktiert`)
  if (waitingCount > 0) parts.push(`${waitingCount} wartet`)
  if (pendingCount > 0) parts.push(`${pendingCount} offen`)
  if (notSelectedCount > 0) parts.push(`${notSelectedCount} nicht ausgewählt`)
  
  let label = `Kaskade: ${parts.join(', ')}`
  
  if (viewerRank != null) {
    label += `, du bist an Position ${viewerRank}`
  }
  
  return label
}

function StepPoint({ step, compact }: { step: CascadeStep | { type: 'ellipsis'; count: number }; compact: boolean }) {
  if ('type' in step && step.type === 'ellipsis') {
    return (
      <div className="flex items-center justify-center w-8 h-8 text-gray-400 dark:text-gray-500 text-sm font-medium">
        +{step.count}
      </div>
    )
  }

  const cascadeStep = step as CascadeStep
  const { status, is_me, rank } = cascadeStep
  
  const size = compact ? 'w-6 h-6' : 'w-8 h-8'
  const textSize = compact ? 'text-xs' : 'text-sm'
  
  let pointContent = ''
  let pointClasses = ''
  let label = ''
  
  switch (status) {
    case 'declined':
    case 'cancelled':
      pointContent = '✕'
      pointClasses = 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-2 border-red-500 dark:border-red-400'
      label = status === 'declined' ? 'Abgelehnt' : 'Storniert'
      break
    case 'timed_out':
      pointContent = '⏳'
      pointClasses = 'bg-orange-50 dark:bg-orange-900/20 text-orange-500 dark:text-orange-400 border-2 border-orange-300 dark:border-orange-700 opacity-70'
      label = 'Zeit abgelaufen'
      break
    case 'recontacted_timed_out':
      // prionew-v2: zweites Antwortfenster abgelaufen — Darstellung wie timed_out.
      pointContent = '⏳'
      pointClasses = 'bg-orange-50 dark:bg-orange-900/20 text-orange-500 dark:text-orange-400 border-2 border-orange-300 dark:border-orange-700 opacity-70'
      label = 'Erneute Anfrage abgelaufen'
      break
    case 'recontacted_declined':
      // prionew-v2: Absage auf die zweite Anfragerunde — Darstellung wie declined.
      pointContent = '✕'
      pointClasses = 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-2 border-red-500 dark:border-red-400'
      label = 'Erneute Anfrage abgelehnt'
      break
    case 'contacted':
      pointContent = rank.toString()
      pointClasses = 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-2 border-orange-500 dark:border-orange-400 animate-pulse'
      label = 'Kontaktiert'
      break
    case 'recontacted':
      // prionew-v2: zweite Anfragerunde läuft — aktiv wie contacted.
      pointContent = rank.toString()
      pointClasses = 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-2 border-orange-500 dark:border-orange-400 animate-pulse'
      label = 'Erneut angefragt'
      break
    case 'waiting':
      pointContent = rank.toString()
      pointClasses = 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-2 border-blue-500 dark:border-blue-400'
      label = 'Teilannahme'
      break
    case 'accepted':
      pointContent = '✓'
      pointClasses = 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 border-2 border-green-500 dark:border-green-400'
      label = is_me ? 'Du (angenommen)' : 'Angenommen'
      break
    case 'accepted_not_selected':
      pointContent = '✕'
      pointClasses = 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-2 border-gray-400 dark:border-gray-500'
      label = 'Nicht ausgewählt'
      break
    case 'pending':
    default:
      pointContent = rank.toString()
      pointClasses = 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-2 border-gray-300 dark:border-gray-600'
      label = 'Wartet'
      break
  }
  
  // Override for viewer's own entry: show "Du" instead of rank number,
  // except for 'accepted' status where we keep the checkmark.
  // The rank number is intentionally not visible when is_me is true.
  if (is_me) {
    pointClasses += ' ring-2 ring-blue-500 dark:ring-blue-400'
    if (status !== 'accepted') {
      pointContent = 'Du'
    }
  }
  
  return (
    <div className="flex flex-col items-center gap-1 group cursor-pointer">
      <div
        className={`${size} rounded-full flex items-center justify-center ${textSize} font-semibold ${pointClasses} transition-transform group-hover:scale-110`}
        title={`Priorität ${rank}: ${label}`}
      >
        {pointContent}
      </div>
      <div className="text-xs text-gray-600 dark:text-gray-400 text-center group-hover:font-medium">
        {label}
      </div>
    </div>
  )
}

function Connector() {
  return (
    <div className="flex-1 h-0.5 bg-gray-300 dark:bg-gray-600 min-w-4" />
  )
}
